import React from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { colors } from "../../theme";
import { horizontalScroll } from "../home/homeStyles";
import { COMPARE_MAX_PLAYERS } from "./compareShell";
import { compareChipSx } from "./compareChromeStyles";

export const ComparePageFrame = ({ title, subtitle, children }) => (
  <Box sx={{ p: 3, pt: 0, maxWidth: 1200, mx: "auto" }}>
    <Typography variant="h4" gutterBottom>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
      {subtitle}
    </Typography>
    {children}
  </Box>
);

export const CompareVsSeparator = () => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{ px: 0.25, flexShrink: 0 }}
  >
    vs
  </Typography>
);

export const CompareSlotsBar = ({
  children,
  onAdd,
  canAdd,
  atMax = false,
}) => (
  <Paper sx={{ p: 2, mb: 2 }}>
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        alignItems: "center",
      }}
    >
      {children}
      {canAdd && (
        <Button
          size="small"
          variant="text"
          startIcon={<AddIcon />}
          onClick={onAdd}
          sx={{ flexShrink: 0, ml: 0.5 }}
        >
          Add
        </Button>
      )}
    </Box>
    {atMax && (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, display: "block" }}
      >
        Maximum of {COMPARE_MAX_PLAYERS} players
      </Typography>
    )}
  </Paper>
);

export const CompareQuickAdd = ({
  caption,
  items = [],
  loading = false,
  getKey,
  getLabel,
  getTooltip,
  isSelected,
  onSelect,
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ mb: 0.75, display: "block" }}
    >
      {caption}
    </Typography>
    {loading ? (
      <CircularProgress size={18} />
    ) : (
      <Box sx={horizontalScroll}>
        {items.map((item) => {
          const selected = Boolean(isSelected?.(item));
          const label = getLabel(item);
          const chip = (
            <Chip
              label={label}
              onClick={() => onSelect(item)}
              clickable
              size="small"
              variant={selected ? "filled" : "outlined"}
              color={selected ? "primary" : "default"}
              sx={compareChipSx(selected)}
            />
          );
          const tooltip = getTooltip?.(item);
          return tooltip ? (
            <Tooltip key={getKey(item)} title={tooltip} arrow>
              {chip}
            </Tooltip>
          ) : (
            <React.Fragment key={getKey(item)}>{chip}</React.Fragment>
          );
        })}
      </Box>
    )}
  </Box>
);

export const CompareChartCard = ({
  groups = [],
  groupValue,
  onGroupChange,
  stats = [],
  statValue,
  onStatChange,
  timeframe,
  showTimeframe = false,
  onToggleTimeframe,
  actions,
  children,
}) => (
  <Paper sx={{ p: 2 }}>
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        alignItems: "center",
        justifyContent: "space-between",
        mb: 2,
      }}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
        {groups.length > 0 && (
          <ToggleButtonGroup
            value={groupValue}
            exclusive
            size="small"
            onChange={(_, value) => {
              if (value) onGroupChange(value);
            }}
            sx={{
              "& .MuiToggleButton-root": {
                px: 1.5,
                py: 0.5,
                textTransform: "none",
                borderColor: colors.border,
              },
            }}
          >
            {groups.map((group) => (
              <ToggleButton key={group.value} value={group.value}>
                {group.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="compare-stat-label">Stat</InputLabel>
          <Select
            labelId="compare-stat-label"
            value={statValue}
            label="Stat"
            onChange={(event) => onStatChange(event.target.value)}
          >
            {stats.map((stat) => (
              <MenuItem key={stat.id} value={stat.id}>
                {stat.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {showTimeframe && timeframe && (
          <Chip
            label={timeframe.chipLabel}
            onClick={onToggleTimeframe}
            clickable
            size="small"
            variant={timeframe.id === "careerYear" ? "filled" : "outlined"}
            color={timeframe.id === "careerYear" ? "primary" : "default"}
          />
        )}
      </Box>
      {actions}
    </Box>
    {children}
  </Paper>
);
